import {useId, useRef} from 'react';

import Banner from '@/components/Banner/Banner';
import {AppButton} from '@/components/Button';
import CloseButton from '@/components/CloseButton/CloseButton';
import {StickyPageLayout} from '@/components/Layout';
import PopupResponsive from '@/components/Popup/PopupResponsive';
import PromotedBadge from '@/components/PromotedBadge/PromotedBadge';
import useConfigExternalLink from '@/hooks/externalLink/useConfigExternalLink';
import useIsMobileLayout from '@/hooks/layout/useIsMobileLayout';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import type {AutoDeliveryUserOnboardContent} from '@/types/content/autoDelivery/autoDeliveryUserOnboardContentModel';
import helpers from '@/utils/helpers';
import {twx} from '@/utils/tailwindUtils';
import {WARNING_BANNER_SOURCE_TYPES, WarningBanners, WarningBannersTop} from '@/views/WarningBanners';

import AutoDeliveryDcxIconList from './AutoDeliveryDcxIconList';

interface AutoDeliveryDcxDetailsStatusNoneTemplateProps {
    content?: AutoDeliveryUserOnboardContent;
    amplitudeEvent?: () => void;
    onClose: () => void;
}

const AutoDeliveryDcxDetailsStatusNoneTemplate = (props: AutoDeliveryDcxDetailsStatusNoneTemplateProps) => {
    const {content, amplitudeEvent, onClose} = props;
    const isMobile = useIsMobileLayout();
    const popupRef = useRef<HTMLElement | null>(null);
    const headerId = useId();

    const {banner, firstSectionTitle, firstSectionItems, secondSectionTitle, secondSectionItems, button} =
        content || {};
    const {title, description, promotion, images, videos} = banner || {};
    const videoPath = videos?.[0]?.path;
    const {redirectToExternalLink} = useConfigExternalLink(button?.externalLink);
    const isShowButton = button?.text && redirectToExternalLink;

    useDocumentTitle(title);

    const onButtonClick = () => {
        helpers.runFunction(amplitudeEvent);
        // @ts-expect-error
        redirectToExternalLink();
    };

    const ButtonComponent = ({className, ...rest}: {className?: string; [key: string]: unknown}) => {
        if (!isShowButton) return null;

        return (
            <AppButton
                primary
                light
                className={twx(
                    'mt-0 whitespace-nowrap md:min-w-[345px] [&>*]:overflow-hidden [&>*]:text-ellipsis',
                    className
                )}
                onClick={onButtonClick}
                aria-label={button?.alt}
                {...rest}
            >
                {button?.text}
            </AppButton>
        );
    };

    return (
        // @ts-expect-error
        <PopupResponsive
            contentRef={popupRef}
            className='overflow-auto text-start ml:ml-[calc(var(--sidebar-width)/2)] ml:max-h-[calc(100%-92px-var(--hw-h))] ml:w-[calc(100%-var(--sidebar-width)*1.5)] ml:max-w-[1016px] ml:rounded-xl ml:p-0'
            closeButtonClass='ml:text-romance'
            onClose={onClose}
            aria-labelledby={headerId}
        >
            {isMobile && (
                <WarningBannersTop source={WARNING_BANNER_SOURCE_TYPES.AUTO_DELIVERY} containerRef={popupRef} />
            )}
            {/* @ts-expect-error */}
            <StickyPageLayout
                className='items-center'
                headerOptions={{
                    endIcon: (
                        // @ts-expect-error
                        <CloseButton
                            onClick={onClose}
                            className='text-romance group-data-[is-sticked=true]:text-primary-dark'
                        />
                    ),
                    title,
                }}
                contentClassName='flex flex-1 flex-col items-center'
                footer={isMobile && <ButtonComponent />}
            >
                <Banner
                    className='h-[422px] bg-primary-dark ml:h-[308px]'
                    imageMedia={images}
                    videoPath={videoPath}
                    isGradient
                    gradient='linear-gradient(180deg, rgba(52, 48, 61, 0.00) 39.99%, rgba(28, 26, 33, 0.90) 100%)'
                >
                    <div className='absolute bottom-8 w-full px-4 text-start text-white ml:px-12'>
                        <div className='mb-2 flex ml:mb-4'>
                            <PromotedBadge
                                className='max-w-full whitespace-nowrap [&>*]:overflow-hidden [&>*]:text-ellipsis'
                                badgeText={promotion}
                            />
                        </div>
                        <h1
                            id={headerId}
                            className='mb-2 overflow-hidden text-ellipsis whitespace-nowrap text-28 leading-[40px] ml:mb-4'
                        >
                            {title}
                        </h1>
                        <p className='line-clamp-3 whitespace-pre-line ml:max-w-96 ml:text-14 ml:leading-[20px] ml:tracking-04'>
                            {description}
                        </p>
                        {!isMobile && <ButtonComponent dark className='mt-4' />}
                    </div>
                </Banner>
                <div className='w-full ml:grid ml:grid-cols-2 ml:gap-8 ml:p-8'>
                    <AutoDeliveryDcxIconList
                        title={firstSectionTitle}
                        items={firstSectionItems}
                        className='hidden px-4 py-8 has-[h2,li_*]:flex ml:m-0 ml:p-0'
                    />
                    <AutoDeliveryDcxIconList
                        title={secondSectionTitle}
                        items={secondSectionItems}
                        className='mx-4 hidden border-t border-primary-dark-5 py-8 has-[h2,li_*]:flex ml:mx-0 ml:border-0 ml:p-0'
                    />
                </div>
                {/* @ts-expect-error */}
                {isMobile && <WarningBanners source={WARNING_BANNER_SOURCE_TYPES.AUTO_DELIVERY} />}
            </StickyPageLayout>
        </PopupResponsive>
    );
};

export default AutoDeliveryDcxDetailsStatusNoneTemplate;
